import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  MapPin,
  Phone,
  User,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MedicalProvider,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import { getSimpleTreatmentStatus, SimpleTreatmentStatus } from '../utils';

interface MergeStepPreviewProps {
  destinationProvider: MedicalProvider;
  sourceProviders: MedicalProvider[];
}

export const MergeStepPreview = ({
  destinationProvider,
  sourceProviders,
}: MergeStepPreviewProps) => {
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'NEEDS_APPROVAL':
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            Needs Approval
          </Badge>
        );
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  const getTreatmentStatus = (treatmentStatus: ProviderTreatmentStatus) => {
    const currentStatus = getSimpleTreatmentStatus(treatmentStatus);
    switch (currentStatus) {
      case SimpleTreatmentStatus.TREATING:
        return (
          <Badge className="bg-blue-100 text-blue-800">Still Treating</Badge>
        );
      case SimpleTreatmentStatus.COMPLETED:
        return (
          <Badge className="bg-gray-100 text-gray-800">Not Treating</Badge>
        );
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  const renderProviderSummary = (
    provider: MedicalProvider,
    isDestination = false,
  ) => (
    <Card className={isDestination ? 'ring-2 ring-green-500 bg-green-50' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span className="text-lg font-medium text-gray-900">
            {provider.name || 'Unnamed Provider'}
          </span>
          {isDestination && (
            <Badge className="bg-green-100 text-green-800">Destination</Badge>
          )}
        </CardTitle>
        <div className="flex flex-wrap gap-2">
          {getStatusBadge(provider.status)}
          {getTreatmentStatus(provider.treatment_status)}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-3">
          {provider.address && (
            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-sm text-gray-600">{provider.address}</span>
            </div>
          )}

          {provider.practitioner_names &&
            provider.practitioner_names.length > 0 && (
              <div className="flex items-start space-x-2">
                <User className="w-4 h-4 text-gray-400 mt-0.5" />
                <div className="text-sm text-gray-600">
                  {provider.practitioner_names.join(', ')}
                </div>
              </div>
            )}

          {provider.handles && provider.handles.length > 0 && (
            <div className="flex items-start space-x-2">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="text-sm text-gray-600">
                {provider.handles.map((handle) => handle.value).join(', ')}
              </div>
            </div>
          )}

          {provider.encounters && provider.encounters.length > 0 && (
            <div className="flex items-start space-x-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="text-sm text-gray-600">
                {provider.encounters.length} encounter
                {provider.encounters.length === 1 ? '' : 's'}
                {provider.encounters.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {provider.encounters.slice(0, 3).map((encounter, idx) => (
                      <div key={idx} className="text-xs text-gray-500">
                        {encounter.date
                          ? new Date(encounter.date).toLocaleDateString()
                          : 'No date'}
                        : {encounter.description}
                      </div>
                    ))}
                    {provider.encounters.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{provider.encounters.length - 3} more encounters
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {provider.description && (
            <div className="text-sm text-gray-600">
              <strong>Description:</strong> {provider.description}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const totalEncounters = [destinationProvider, ...sourceProviders].reduce(
    (total, provider) => total + (provider.encounters?.length || 0),
    0,
  );

  const totalContacts = [destinationProvider, ...sourceProviders].reduce(
    (total, provider) => total + (provider.handles?.length || 0),
    0,
  );

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-lg font-medium text-gray-900 mb-2">
          Review Merge Operation
        </h2>
        <p className="text-sm text-gray-600">
          The following providers will be merged. This action cannot be undone.
        </p>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-900">Important</h3>
            <p className="text-sm text-yellow-800 mt-1">
              This merge operation cannot be undone. All data from the source
              providers will be combined into the destination provider, and the
              source providers will be deleted.
            </p>
          </div>
        </div>
      </div>

      {/* Merge Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-3">Merge Summary</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {sourceProviders.length + 1}
            </div>
            <div className="text-blue-800">Providers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {totalEncounters}
            </div>
            <div className="text-blue-800">Total Encounters</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {totalContacts}
            </div>
            <div className="text-blue-800">Total Contacts</div>
          </div>
        </div>
      </div>

      {/* Destination Provider */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-3">
          Destination Provider (will remain)
        </h3>
        {renderProviderSummary(destinationProvider, true)}
      </div>

      {/* Source Providers */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-3">
          Source Providers (will be merged in)
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          {sourceProviders.map((provider) => (
            <div key={provider.id} className="relative">
              {renderProviderSummary(provider)}
              <div className="absolute -right-8 top-1/2 transform -translate-y-1/2 hidden md:block">
                <ArrowRight className="w-6 h-6 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Result Preview */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-3">After Merge</h3>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            <strong>{destinationProvider.name}</strong> will contain all
            information from {sourceProviders.length} additional provider
            {sourceProviders.length === 1 ? '' : 's'}:
          </p>
          <ul className="list-disc list-inside text-sm text-green-800 mt-2 ml-4">
            <li>All encounters will be preserved and consolidated</li>
            <li>All contact information will be merged</li>
            <li>All practitioner names will be combined</li>
            <li>Descriptions will be merged intelligently</li>
            <li>Tasks will be transferred to the destination provider</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
